import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useWalkthrough } from "src/contexts/WalkthroughContext";
import { useStore } from "src/stores/store";
import { WALKTHROUGH_STEPS } from "./walkthrough-steps";

const WalkthroughComplete: FC = () => {
  const { t } = useTranslation("walkthrough");
  const { isActive, currentStep, endTour } = useWalkthrough();
  const { globalStore } = useStore();

  const lastStepIndex = WALKTHROUGH_STEPS.length - 1;
  const isVisible = isActive && currentStep === lastStepIndex;

  const handleFinish = () => {
    endTour();
    globalStore.showSuccess(t("completion-toast"));
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <motion.div
            className="relative w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {/* Header accent */}
            <div className="h-1.5 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500" />

            <div className="p-6 sm:p-8 text-center">
              {/* Celebration icon */}
              <motion.div
                className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                  delay: 0.1,
                }}
              >
                <CheckCircle2 className="h-7 w-7 text-green-600" />
              </motion.div>

              {/* Title */}
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {t("steps.complete.title")}
              </h2>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                {t("steps.complete.description")}
              </p>

              {/* Action */}
              <button
                onClick={handleFinish}
                className="inline-flex items-center justify-center rounded-lg bg-green-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                {t("navigation.finish")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default WalkthroughComplete;
